/**
 * Study store for managing current study and global filters.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Study {
  studyId: string
  name: string
  phase: string
  status: string
  description?: string
  therapeuticArea?: string
  sponsor?: string
  domains?: string[]
  lastDataLoad?: string
  createdAt?: string
  updatedAt?: string
  meddraVersion?: string
}

export interface FilterValue {
  operator: 'eq' | 'ne' | 'in' | 'not_in' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'between'
  value: string | number | string[] | number[] | [number, number]
}

export interface GlobalFilter {
  column: string
  label: string
  value: FilterValue
}

interface StudyState {
  // Current study
  currentStudy: Study | null
  recentStudies: Study[]

  // Global filters (apply across all data views)
  globalFilters: GlobalFilter[]

  // Available filter values (loaded from backend)
  filterOptions: Record<string, string[]>

  // Loading state
  isLoading: boolean

  // Actions
  setCurrentStudy: (study: Study | null) => void
  addRecentStudy: (study: Study) => void
  removeFromRecent: (studyId: string) => void
  clearRecentStudies: () => void

  // Filter actions
  setGlobalFilter: (filter: GlobalFilter) => void
  removeGlobalFilter: (column: string) => void
  clearGlobalFilters: () => void
  setFilterOptions: (column: string, options: string[]) => void

  setLoading: (loading: boolean) => void
}

const MAX_RECENT_STUDIES = 5

export const useStudyStore = create<StudyState>()(
  persist(
    (set, get) => ({
      currentStudy: null,
      recentStudies: [],
      globalFilters: [],
      filterOptions: {},
      isLoading: false,

      setCurrentStudy: (study) => {
        if (study) {
          // Add to recent studies
          get().addRecentStudy(study)
        }
        // Clear global filters when switching studies
        set({ currentStudy: study, globalFilters: [] })
      },

      addRecentStudy: (study) =>
        set((state) => {
          // Remove if already exists
          const filtered = state.recentStudies.filter(
            (s) => s.studyId !== study.studyId
          )
          // Add to front and limit
          const updated = [study, ...filtered].slice(0, MAX_RECENT_STUDIES)
          return { recentStudies: updated }
        }),

      removeFromRecent: (studyId) =>
        set((state) => ({
          recentStudies: state.recentStudies.filter((s) => s.studyId !== studyId),
          // Also clear currentStudy if it matches
          currentStudy: state.currentStudy?.studyId === studyId ? null : state.currentStudy,
        })),

      clearRecentStudies: () => set({ recentStudies: [] }),

      setGlobalFilter: (filter) =>
        set((state) => {
          // Replace existing filter for same column
          const filtered = state.globalFilters.filter(
            (f) => f.column !== filter.column
          )
          return { globalFilters: [...filtered, filter] }
        }),

      removeGlobalFilter: (column) =>
        set((state) => ({
          globalFilters: state.globalFilters.filter((f) => f.column !== column),
        })),

      clearGlobalFilters: () => set({ globalFilters: [] }),

      setFilterOptions: (column, options) =>
        set((state) => ({
          filterOptions: { ...state.filterOptions, [column]: options },
        })),

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'sage-study',
      partialize: (state) => ({
        currentStudy: state.currentStudy,
        recentStudies: state.recentStudies,
        // Don't persist global filters or filter options
      }),
    }
  )
)

// Selectors for common patterns
export const useCurrentStudy = () => useStudyStore((state) => state.currentStudy)
export const useGlobalFilters = () => useStudyStore((state) => state.globalFilters)
export const useStudyLoading = () => useStudyStore((state) => state.isLoading)
